import { useCallback, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { OpsShell } from "@/components/admin/OpsShell";
import { RangeFilter } from "@/components/RangeFilter";
import { rangeQuery, labelFor } from "@/lib/dateRange";
import { axis, Btn, Card, chartTip, Empty, gridStroke, KPI, Loading, T, usePoll, useOpsTheme } from "@/components/admin/ui";

const SLICE = ["#10b981", "#3b82f6", "#8b5cf6", "#f59e0b", "#ec4899", "#14b8a6"];

export default function AdminDashboard() {
  const { dark } = useOpsTheme();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState({ preset: "30d", custom: {} });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/overview?${rangeQuery(range.preset, range.custom)}`);
      setD(data);
    } finally {
      setBusy(false);
    }
  }, [range]);
  usePoll(load, 30000);

  const dist = d ? Object.entries(d.subscriptions.distribution).map(([name, value]) => ({ name, value })) : [];
  const spark = d?.charts?.sparklines || [];

  return (
    <OpsShell
      title="Overview"
      subtitle="Everything happening across Propojo · refreshes every 30s"
      actions={
        <div className="flex items-center gap-2">
          <RangeFilter
            variant="ops"
            testid="admin-range"
            preset={range.preset}
            custom={range.custom}
            onChange={setRange}
          />
          <Btn data-testid="admin-refresh-overview" onClick={load}>
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Btn>
        </div>
      }
    >
      {!d ? (
        <Loading label="Fetching platform stats" />
      ) : (
        <div className="space-y-5" data-testid="admin-dashboard">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="admin-period-kpis">
            <KPI testid="period-label" label="Selected timeline" value={labelFor(range.preset, range.custom)} footer={d.period.all_time ? "Everything ever recorded" : `${d.period.from} → ${d.period.to}`} />
            <KPI testid="period-users" label="New customers" value={d.period.users} />
            <KPI testid="period-proposals" label="Proposals created" value={d.period.proposals} />
            <KPI testid="period-revenue" label="Revenue collected" value={`₹${d.period.revenue.toLocaleString("en-IN")}`} footer={`${d.period.failed_payments} failed payment(s)`} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            <KPI testid="stat-revenue" label="Total revenue" value={d.revenue.total_formatted} delta={d.revenue.delta} spark={spark} dataKey="revenue" tone="emerald" />
            <KPI testid="stat-mrr" label="Monthly recurring revenue" value={d.revenue.mrr_formatted} footer={`${d.subscriptions.active} active · ${d.users.lifetime} lifetime`} />
            <KPI testid="stat-total-users" label="Total customers" value={d.users.total} delta={d.users.delta} spark={spark} dataKey="users" />
            <KPI testid="stat-proposals" label="Proposals created" value={d.proposals.total} delta={d.proposals.delta} spark={spark} dataKey="proposals" />
            <KPI testid="stat-active-users" label="Active last 7 days" value={d.users.active_7d} footer={`${d.users.new_today} joined today · ${d.users.new_month} this month`} />
            <KPI testid="stat-api-hits" label="API calls today" value={d.api.hits_today} delta={d.api.delta} spark={spark} dataKey="api" footer={`${d.api.avg_response_ms}ms average`} />
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <Card testid="chart-growth" title="Growth" hint="New customers and proposals, last 14 days" className="lg:col-span-2">
              <div className="h-[260px] p-4 pt-5">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <AreaChart data={d.charts.growth} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gU" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gP" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.22} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={gridStroke(dark)} vertical={false} />
                    <XAxis dataKey="date" {...axis(dark)} />
                    <YAxis {...axis(dark)} width={44} />
                    <Tooltip {...chartTip(dark)} />
                    <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    <Area type="monotone" name="Customers" dataKey="users" stroke="#10b981" strokeWidth={2} fill="url(#gU)" />
                    <Area type="monotone" name="Proposals" dataKey="proposals" stroke="#3b82f6" strokeWidth={2} fill="url(#gP)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card testid="chart-plans" title="Plan mix" hint="Workspaces by plan">
              {dist.length === 0 ? (
                <Empty label="No workspaces yet" />
              ) : (
                <>
                  <div className="h-[196px] p-2 pt-4">
                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                      <PieChart>
                        <Pie data={dist} dataKey="value" nameKey="name" innerRadius={46} outerRadius={74} paddingAngle={2} stroke="none">
                          {dist.map((slice, i) => (
                            <Cell key={slice.name} fill={SLICE[i % SLICE.length]} />
                          ))}
                        </Pie>
                        <Tooltip {...chartTip(dark)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="px-4 pb-4 space-y-1.5">
                    {dist.map((s, i) => (
                      <li key={s.name} className="flex items-center gap-2 text-[13px]">
                        <i className="w-2 h-2 rounded-full shrink-0" style={{ background: SLICE[i % SLICE.length] }} />
                        <span className="capitalize text-slate-700 dark:text-slate-300">{s.name}</span>
                        <span className={`ml-auto ${T.num} ${T.muted}`}>{s.value}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Card>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <Card testid="chart-revenue" title="Revenue by day" hint="Captured payments in ₹, last 14 days" className="lg:col-span-2">
              <div className="h-[220px] p-4 pt-5">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <BarChart data={d.charts.growth} margin={{ top: 4, right: 8, left: -14, bottom: 0 }}>
                    <CartesianGrid stroke={gridStroke(dark)} vertical={false} />
                    <XAxis dataKey="date" {...axis(dark)} />
                    <YAxis {...axis(dark)} width={52} tickFormatter={(v) => `₹${v}`} />
                    <Tooltip {...chartTip(dark)} formatter={(v) => [`₹${v}`, "Revenue"]} />
                    <Bar dataKey="revenue" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={26} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card testid="api-health" title="Health today" hint="Request outcomes and account state">
              <div className="p-4 grid grid-cols-2 gap-3">
                {[
                  ["2xx success", d.api.success_today, "text-emerald-600 dark:text-emerald-400"],
                  ["4xx client", d.api.errors_4xx, "text-amber-600 dark:text-amber-400"],
                  ["5xx server", d.api.errors_5xx, d.api.errors_5xx ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"],
                  ["Peak / minute", d.api.peak_per_minute, ""],
                  ["Failed payments", d.revenue.failed_payments, d.revenue.failed_payments ? "text-rose-600 dark:text-rose-400" : ""],
                  ["Disabled accounts", d.users.disabled, ""],
                ].map(([l, v, c]) => (
                  <div key={l} className="rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2.5">
                    <p className={T.label}>{l}</p>
                    <p className={`mt-1 text-lg font-semibold ${T.num} ${c}`}>{v}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}
    </OpsShell>
  );
}
